'use client';

import { c as _c } from "react/compiler-runtime";
import { MathLib } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { type InterfaceToken, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { InfoTooltip } from '@morpho-org/ui-v6/client';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Separator } from '@repo/ui';
import { InfoHoverCardTrigger } from '@/components/common/InfoHoverCardTrigger';
import { getParsedValue } from '@/helpers/formatters';
import { splitValueAndUnit } from '@/helpers/number';
import { trackMetricTooltipOpenChange } from '@/lib/analytics/client/trackers/metric-tooltip';
import type { VaultHeroData } from './VaultHero.mapper';
interface VaultV1LiquidityHoverCardProps {
  vault: VaultHeroData;
  asset: InterfaceToken | null | undefined;
}
export const VaultV1LiquidityHoverCard = t0 => {
  const $ = _c(61);
  const {
    vault,
    asset
  } = t0;
  let t1;
  if ($[0] !== asset?.symbol || $[1] !== vault.state.assetDecimals) {
    t1 = value => formatters.short.digits(2).trailingZero(false).default("-").unit(asset?.symbol).of(value, vault.state.assetDecimals);
    $[0] = asset?.symbol;
    $[1] = vault.state.assetDecimals;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const formatAssetAmount = t1;
  let t2;
  if ($[3] !== formatAssetAmount || $[4] !== vault.liquidity.underlying) {
    const liquidity = toBigInt(vault.liquidity.underlying);
    t2 = formatAssetAmount(liquidity);
    $[3] = formatAssetAmount;
    $[4] = vault.liquidity.underlying;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const formattedLiquidity = t2;
  let t3;
  if ($[6] !== vault.liquidity.usd) {
    bb0: {
      const value_0 = getParsedValue(vault.liquidity.usd, safeParseNumber);
      if (value_0 == null) {
        t3 = null;
        break bb0;
      }
      t3 = splitValueAndUnit(formatters.short.digits(2).trailingZero(false).default("-").of(value_0, 18));
    }
    $[6] = vault.liquidity.usd;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const formattedLiquidityUsd = t3;
  let idleLiquidity = ZERO;
  let marketLiquidity = ZERO;
  let formattedIdleLiquidity;
  let formattedMarketLiquidity;
  let totalUnderlyingLiquidity;
  if ($[8] !== formatAssetAmount || $[9] !== idleLiquidity || $[10] !== marketLiquidity || $[11] !== vault.allocation) {
    const marketLiquidityMap = new Map();
    for (const alloc of vault.allocation) {
      const isIdle = !alloc.market.collateralAsset;
      const supplyAssets = toBigInt(alloc.supplyAssets) ?? ZERO;
      if (isIdle) {
        idleLiquidity = idleLiquidity + supplyAssets;
      } else {
        const underlyingLiquidity = toBigInt(alloc.market.state?.liquidityAssets) ?? ZERO;
        marketLiquidity = marketLiquidity + MathLib.min(supplyAssets, underlyingLiquidity);
        const key = alloc.market.marketId;
        if (!marketLiquidityMap.has(key)) {
          marketLiquidityMap.set(key, underlyingLiquidity);
        }
      }
    }
    let t4;
    if ($[17] !== formatAssetAmount || $[18] !== idleLiquidity) {
      t4 = formatAssetAmount(idleLiquidity);
      $[17] = formatAssetAmount;
      $[18] = idleLiquidity;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    formattedIdleLiquidity = t4;
    let t5;
    if ($[20] !== formatAssetAmount || $[21] !== marketLiquidity) {
      t5 = formatAssetAmount(marketLiquidity);
      $[20] = formatAssetAmount;
      $[21] = marketLiquidity;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    formattedMarketLiquidity = t5;
    totalUnderlyingLiquidity = ZERO;
    for (const value_1 of marketLiquidityMap.values()) {
      totalUnderlyingLiquidity = totalUnderlyingLiquidity + value_1;
    }
    $[8] = formatAssetAmount;
    $[9] = idleLiquidity;
    $[10] = marketLiquidity;
    $[11] = vault.allocation;
    $[12] = formattedIdleLiquidity;
    $[13] = formattedMarketLiquidity;
    $[14] = totalUnderlyingLiquidity;
    $[15] = idleLiquidity;
    $[16] = marketLiquidity;
  } else {
    formattedIdleLiquidity = $[12];
    formattedMarketLiquidity = $[13];
    totalUnderlyingLiquidity = $[14];
    idleLiquidity = $[15];
    marketLiquidity = $[16];
  }
  let t4;
  if ($[23] !== formatAssetAmount || $[24] !== totalUnderlyingLiquidity) {
    t4 = formatAssetAmount(totalUnderlyingLiquidity);
    $[23] = formatAssetAmount;
    $[24] = totalUnderlyingLiquidity;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  const formattedTotalUnderlyingLiquidity = t4;
  let t5;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="text-foreground text-sm leading-none">Liquidity</span>;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== formattedLiquidity) {
    t6 = <div className="px-sm gap-y-2xs flex min-h-8 flex-wrap items-center justify-between gap-x-4 py-[1px]">{t5}<span className="text-foreground text-sm leading-none">{formattedLiquidity}</span></div>;
    $[27] = formattedLiquidity;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="px-sm my-1.5"><Separator /></div>;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="text-2xs text-secondary-foreground leading-none">Market Liquidity</span>;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== formattedMarketLiquidity) {
    t9 = <div className="px-sm gap-y-2xs flex min-h-8 flex-wrap items-center justify-between gap-x-4 py-[1px]">{t8}<span className="text-2xs text-secondary-foreground leading-none">{formattedMarketLiquidity}</span></div>;
    $[31] = formattedMarketLiquidity;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-2xs text-secondary-foreground leading-none">Idle Liquidity</span>;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== formattedIdleLiquidity) {
    t11 = <div className="px-sm gap-y-2xs flex min-h-8 flex-wrap items-center justify-between gap-x-4 py-[1px]">{t10}<span className="text-2xs text-secondary-foreground leading-none">{formattedIdleLiquidity}</span></div>;
    $[34] = formattedIdleLiquidity;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="px-sm my-1.5"><Separator /></div>;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="gap-2xs flex items-center"><span className="text-2xs text-secondary-foreground leading-none">Total Underlying Liquidity</span><InfoTooltip tooltipContent="The sum of the underlying liquidity across all markets this vault is exposed to." /></div>;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== formattedTotalUnderlyingLiquidity) {
    t14 = <div className="px-sm gap-y-2xs flex min-h-8 flex-wrap items-center justify-between gap-x-4 py-[1px]">{t13}<span className="text-2xs text-secondary-foreground leading-none">{formattedTotalUnderlyingLiquidity}</span></div>;
    $[38] = formattedTotalUnderlyingLiquidity;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t11 || $[41] !== t14 || $[42] !== t6 || $[43] !== t9) {
    t15 = <div className="py-sm min-w-0">{t6}{t7}{t9}{t11}{t12}{t14}</div>;
    $[40] = t11;
    $[41] = t14;
    $[42] = t6;
    $[43] = t9;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const hoverCardContent = t15;
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="text-secondary-foreground text-xs" style={{
      whiteSpace: "nowrap"
    }}>Liquidity</span>;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== hoverCardContent) {
    t17 = <div className="flex items-center gap-[6px]">{t16}<InfoHoverCardTrigger maxWidth="400px" onOpenChange={_temp}>{hoverCardContent}</InfoHoverCardTrigger></div>;
    $[46] = hoverCardContent;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const t18 = formattedLiquidityUsd ? `$${formattedLiquidityUsd.value}` : "-";
  let t19;
  if ($[48] !== t18) {
    t19 = <span className="text-title-sm">{t18}</span>;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const t20 = formattedLiquidityUsd?.unit;
  let t21;
  if ($[50] !== t20) {
    t21 = <span className="text-title-sm text-muted-foreground">{t20}</span>;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t19 || $[53] !== t21) {
    t22 = <div className="flex items-center">{t19}{t21}</div>;
    $[52] = t19;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== formattedLiquidity) {
    t23 = <span className="text-xs">{formattedLiquidity}</span>;
    $[55] = formattedLiquidity;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t17 || $[58] !== t22 || $[59] !== t23) {
    t24 = <div className="gap-sm flex flex-1 flex-col">{t17}{t22}{t23}</div>;
    $[57] = t17;
    $[58] = t22;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  return t24;
};
function _temp(isOpen) {
  return trackMetricTooltipOpenChange(isOpen, {
    page: "vault",
    metric: "liquidity",
    vault_version: "v1"
  });
}